// src/pages/TracePage.js
// Public product passport — loaded by scanning a QR code on a label.
// No login required. Mobile-first, max-width 480px.
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Minimal bilingual helper — reads from localStorage so a logged-in user's
// language preference carries over; falls back to browser language.
const initLang = () => {
  const saved = localStorage.getItem('pizzacini_language');
  if (saved === 'es' || saved === 'en') return saved;
  return navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en';
};
const tr = (en, es, lang) => (lang === 'es' && es ? es : en);

const EXP_LABEL_DISPLAY = {
  en: { best_by: 'Best By', use_by: 'Use By', exp: 'Exp.', best_if_used_by: 'Best If Used By' },
  es: { best_by: 'Consumir Antes de', use_by: 'Usar Antes de', exp: 'Vence', best_if_used_by: 'Mejor Si se Usa Antes de' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

// ── Allergen Badges ────────────────────────────────────────────────────────────

function AllergenBadges({ allergenList }) {
  if (!allergenList) return null;
  // Parse comma-separated allergens
  const items = allergenList.split(',').map(s => s.trim()).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map(a => (
        <span key={a} style={{
          padding: '3px 10px', borderRadius: 12,
          background: '#fef3c7', border: '1px solid #fcd34d',
          color: '#92400e', fontSize: 12, fontWeight: 600,
        }}>
          ⚠ {a}
        </span>
      ))}
    </div>
  );
}

// ── Nutrition Facts ────────────────────────────────────────────────────────────

function NutritionRow({ label, value, bold, indent, dvPercent, thick }) {
  return (
    <tr style={{ borderTop: thick ? '4px solid #000' : '1px solid #000' }}>
      <td style={{ paddingLeft: indent ? 16 : 0, fontWeight: bold ? 700 : 400, fontSize: 12, paddingTop: 2, paddingBottom: 2 }}>
        {label}
        {value != null && <span style={{ fontWeight: 400 }}> {value}</span>}
      </td>
      {dvPercent != null && (
        <td style={{ textAlign: 'right', fontWeight: bold ? 700 : 400, fontSize: 12, paddingTop: 2, paddingBottom: 2 }}>
          {dvPercent}%
        </td>
      )}
    </tr>
  );
}

function NutritionFactsPanel({ nf }) {
  if (!nf) return null;
  return (
    <div style={{ border: '2px solid #000', padding: 8, fontFamily: 'Arial, sans-serif', maxWidth: 300 }}>
      <div style={{ fontSize: 24, fontWeight: 900, borderBottom: '8px solid #000', paddingBottom: 4, marginBottom: 4 }}>
        Nutrition Facts
      </div>
      {nf.servingsPerContainer && (
        <div style={{ fontSize: 12 }}>{nf.servingsPerContainer.en || nf.servingsPerContainer}</div>
      )}
      {nf.servingSize && (
        <div style={{ fontWeight: 700, fontSize: 14, borderBottom: '4px solid #000', paddingBottom: 4, marginBottom: 2 }}>
          Serving size {nf.servingSize.en || nf.servingSize}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: '8px solid #000', marginTop: 4 }}>
        <tbody>
          <tr><td colSpan={2} style={{ fontSize: 10, fontWeight: 400, paddingBottom: 2 }}>Amount per serving</td></tr>
          <NutritionRow label="Calories" value={nf.calories} bold thick />
          <tr style={{ borderTop: '4px solid #000' }}>
            <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: 10, paddingBottom: 2 }}>% Daily Value*</td>
          </tr>
          {nf.totalFat && <NutritionRow label="Total Fat" value={`${nf.totalFat.g}g`} bold dvPercent={nf.totalFat.dv} />}
          {nf.saturatedFat && <NutritionRow label="Saturated Fat" value={`${nf.saturatedFat.g}g`} indent dvPercent={nf.saturatedFat.dv} />}
          {nf.transFat != null && <NutritionRow label="Trans Fat" value={`${nf.transFat.g ?? nf.transFat}g`} indent />}
          {nf.cholesterol && <NutritionRow label="Cholesterol" value={`${nf.cholesterol.mg}mg`} bold dvPercent={nf.cholesterol.dv} thick />}
          {nf.sodium && <NutritionRow label="Sodium" value={`${nf.sodium.mg}mg`} bold dvPercent={nf.sodium.dv} thick />}
          {nf.totalCarbohydrate && <NutritionRow label="Total Carbohydrate" value={`${nf.totalCarbohydrate.g}g`} bold dvPercent={nf.totalCarbohydrate.dv} thick />}
          {nf.dietaryFiber && <NutritionRow label="Dietary Fiber" value={`${nf.dietaryFiber.g}g`} indent dvPercent={nf.dietaryFiber.dv} />}
          {nf.totalSugars && <NutritionRow label="Total Sugars" value={`${nf.totalSugars.g}g`} indent />}
          {nf.addedSugars && <NutritionRow label="Includes Added Sugars" value={`${nf.addedSugars.g}g`} indent dvPercent={nf.addedSugars.dv} />}
          {nf.protein && <NutritionRow label="Protein" value={`${nf.protein.g}g`} bold thick />}
          {nf.vitaminD && <NutritionRow label="Vitamin D" value={`${nf.vitaminD.mcg}mcg`} dvPercent={nf.vitaminD.dv} thick />}
          {nf.calcium && <NutritionRow label="Calcium" value={`${nf.calcium.mg}mg`} dvPercent={nf.calcium.dv} />}
          {nf.iron && <NutritionRow label="Iron" value={`${nf.iron.mg}mg`} dvPercent={nf.iron.dv} />}
          {nf.potassium && <NutritionRow label="Potassium" value={`${nf.potassium.mg}mg`} dvPercent={nf.potassium.dv} />}
        </tbody>
      </table>
      <div style={{ fontSize: 9, marginTop: 6, borderTop: '1px solid #000', paddingTop: 4 }}>
        * The % Daily Value (DV) tells you how much a nutrient in a serving contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
      </div>
    </div>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────────

function Badge({ label, active }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 14px', borderRadius: 20,
      background: active ? '#d1fae5' : '#f3f4f6',
      color: active ? '#065f46' : '#9ca3af',
      fontSize: 13, fontWeight: 600,
      border: `1px solid ${active ? '#6ee7b7' : '#e5e7eb'}`,
    }}>
      <span>{active ? '✓' : '○'}</span>
      {label}
    </div>
  );
}

// ── Section ────────────────────────────────────────────────────────────────────

function Section({ icon, title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontWeight: 700, fontSize: 12, marginBottom: 10,
        display: 'flex', alignItems: 'center', gap: 7,
        color: '#374151', letterSpacing: '0.01em',
        textTransform: 'uppercase',
      }}>
        <span style={{ fontSize: 16 }}>{icon}</span> {title}
      </div>
      {children}
    </div>
  );
}

function InfoCard({ children }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', fontSize: 13 }}>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ color: '#6b7280' }}>{label}: </span>
      <strong>{value}</strong>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function TracePage() {
  const { lotCode, productId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null); // 'LOT_NOT_FOUND' | 'PRODUCT_NOT_IN_BATCH'
  const [lang, setLang] = useState(initLang);
  const t = (en, es) => tr(en, es, lang);

  useEffect(() => {
    const url = productId
      ? `${API_URL}/api/public/trace/${encodeURIComponent(lotCode)}/${encodeURIComponent(productId)}`
      : `${API_URL}/api/public/trace/${encodeURIComponent(lotCode)}`;
    axios.get(url)
      .then(r => setData(r.data))
      .catch(err => {
        const code = err.response?.data?.code;
        setErrorCode(code || (err.response?.status === 404 ? 'LOT_NOT_FOUND' : 'ERROR'));
      })
      .finally(() => setLoading(false));
  }, [lotCode, productId]);

  const containerStyle = {
    maxWidth: 480,
    margin: '0 auto',
    padding: '20px 16px 48px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#111827',
    lineHeight: 1.5,
    WebkitFontSmoothing: 'antialiased',
  };

  const langToggle = (
    <button
      onClick={() => setLang(l => l === 'en' ? 'es' : 'en')}
      style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
    >
      {lang === 'en' ? 'ES' : 'EN'}
    </button>
  );

  if (loading) {
    return (
      <div style={{ ...containerStyle, position: 'relative' }}>
        {langToggle}
        <div style={{ textAlign: 'center', paddingTop: 80, color: '#6b7280' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          {t('Loading product information…', 'Cargando información del producto…')}
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (errorCode || !data) {
    const isWrongProduct = errorCode === 'PRODUCT_NOT_IN_BATCH';
    return (
      <div style={{ ...containerStyle, position: 'relative' }}>
        {langToggle}
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 52 }}>{isWrongProduct ? '📦' : '🔍'}</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
            {isWrongProduct ? t('Product Not In This Batch', 'Producto No Encontrado en Este Lote') : t('Lot Code Not Found', 'Código de Lote No Encontrado')}
          </div>
          <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, maxWidth: 320, margin: '0 auto' }}>
            {isWrongProduct ? (
              <>
                {t('Lot code', 'El lote')} <strong>{lotCode}</strong> {t('exists, but this product was not produced in that batch.', 'existe, pero este producto no fue producido en ese lote.')}
                <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>
                  {t('If you scanned a label, it may have been misprinted or the URL was manually changed.', 'Si escaneó una etiqueta, puede que esté mal impresa o que la URL haya sido modificada.')}
                </div>
              </>
            ) : (
              <>
                {t('No batch found for lot code', 'No se encontró ningún lote con el código')} <strong>{lotCode}</strong>.
                <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>
                  {t('If you scanned a label, please check that the QR code is intact and try again.', 'Si escaneó una etiqueta, verifique que el código QR esté en buen estado e intente de nuevo.')}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { product, batch, label, printJob, production, documents, shipping, company, badges, rawMaterials } = data;
  const expirationDate = printJob?.expirationDate || null;
  const productionDate = batch?.productionDate || printJob?.productionDate || null;
  const expLabelText = (EXP_LABEL_DISPLAY[lang] || EXP_LABEL_DISPLAY.en)[label?.expirationLabel] || t('Best By', 'Consumir Antes de');

  return (
    <div style={{ ...containerStyle, position: 'relative' }}>
      {langToggle}

      {/* ── Header ── */}
      <div style={{ textAlign: 'center', marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        {company?.logoUrl && (
          <img
            src={company.logoUrl}
            alt={company.name}
            style={{ maxHeight: 60, maxWidth: 200, marginBottom: 14, objectFit: 'contain' }}
          />
        )}
        <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{product?.name || 'Product'}</div>
        {label?.netWeight && (
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Net Wt. {label.netWeight}</div>
        )}
        {product?.sku && (
          <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>SKU: {product.sku}</div>
        )}

        {/* Lot / Date / Expiry pills */}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ background: '#f3f4f6', borderRadius: 10, padding: '8px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{t('Lot Code', 'Lote', lang)}</div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{batch?.lotCode || lotCode}</div>
          </div>
          {productionDate && (
            <div style={{ background: '#f3f4f6', borderRadius: 10, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{t('Produced', 'Producido', lang)}</div>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{fmtDate(productionDate)}</div>
            </div>
          )}
          {expirationDate && (
            <div style={{ background: '#fef3c7', borderRadius: 10, padding: '8px 14px', textAlign: 'center', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: 10, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{expLabelText}</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#92400e' }}>{fmtDate(expirationDate)}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Quality Badges ── */}
      {badges && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
          {badges.qualityVerified && <Badge label={t('Quality Verified', 'Calidad Verificada')} active />}
          <Badge label={t('Lab Tested', 'Pruebas de Laboratorio')} active={badges.labTested} />
          <Badge label={t('Cold Chain', 'Cadena de Frío')} active={badges.coldChainMaintained} />
        </div>
      )}

      {/* ── Allergen Alert ── */}
      {(label?.allergenList || label?.allergens) && (
        <Section icon="⚠️" title={t('Allergen Information', 'Información de Alérgenos')}>
          <div style={{ background: '#fffbeb', borderRadius: 10, padding: '12px 14px', border: '1px solid #fde68a' }}>
            {label.allergenList && (
              <div style={{ marginBottom: label.allergens ? 10 : 0 }}>
                <AllergenBadges allergenList={label.allergenList} />
              </div>
            )}
            {label.allergens && (
              <div style={{ fontSize: 13, color: '#78350f' }}>{label.allergens}</div>
            )}
          </div>
        </Section>
      )}

      {/* ── Ingredients ── */}
      {label?.ingredients && (
        <Section icon="📋" title={t('Ingredients', 'Ingredientes')}>
          <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.6 }}>
            {label.ingredients}
          </div>
        </Section>
      )}

      {/* ── Raw Materials ── */}
      {rawMaterials && rawMaterials.length > 0 && (
        <Section icon="🧪" title={t('Ingredients Used', 'Ingredientes Utilizados')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rawMaterials.map((rm, i) => (
              <div key={i} style={{
                background: '#f9fafb', borderRadius: 8, padding: '8px 12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{rm.name}</div>
                  {rm.manufacturer && (
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{rm.manufacturer}</div>
                  )}
                </div>
                {rm.lotCode && (
                  <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: 6, padding: '2px 8px',
                    fontSize: 11, fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap',
                  }}>
                    {t('Lot', 'Lote')} {rm.lotCode}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Nutrition Facts ── */}
      {label?.nutritionFacts && (
        <Section icon="🔬" title={t('Nutrition Facts', 'Datos de Nutrición')}>
          <NutritionFactsPanel nf={label.nutritionFacts} />
        </Section>
      )}

      {/* ── Cooking Instructions ── */}
      {label?.cookingInstructions && (
        <Section icon="🍕" title={t('Cooking Instructions', 'Instrucciones de Cocción')}>
          <InfoCard>
            <div style={{ whiteSpace: 'pre-line', fontSize: 13 }}>{label.cookingInstructions}</div>
          </InfoCard>
        </Section>
      )}

      {/* ── Storage ── */}
      {label?.storageInstructions && (
        <Section icon="❄️" title={t('Storage', 'Almacenamiento')}>
          <InfoCard>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{label.storageInstructions}</div>
          </InfoCard>
        </Section>
      )}

      {/* ── Production ── */}
      <Section icon="🏭" title={t('Production', 'Producción')}>
        <InfoCard>
          <InfoRow label={t('Produced', 'Producido')} value={productionDate ? fmtDate(productionDate) : null} />
          <InfoRow label={t('Lot Code', 'Lote')} value={batch?.lotCode || lotCode} />
          {production?.coldChainCertified && <InfoRow label={t('Blast Frozen', 'Congelado Rápido')} value={t('Yes — rapid freezing after baking', 'Sí — congelación rápida tras el horneado')} />}
          <InfoRow label={t('Packaged by', 'Empacado por')} value={production?.packager} />
          {label?.countryOfOrigin && <InfoRow label={t('Origin', 'Origen')} value={label.countryOfOrigin} />}
        </InfoCard>
      </Section>

      {/* ── Quality Documents ── */}
      {documents && documents.length > 0 && (
        <Section icon="📄" title={t('Quality Documents', 'Documentos de Calidad')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {documents.map(doc => (
              <a
                key={doc.id}
                href={doc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderRadius: 10,
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  textDecoration: 'none', color: '#065f46',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{doc.name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {doc.categoryName}
                    {doc.issueDate && ` · ${fmtDate(doc.issueDate)}`}
                  </div>
                </div>
                <span style={{ fontSize: 18, marginLeft: 8 }}>↗</span>
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* ── Shipping ── */}
      {shipping && (
        <Section icon="🚚" title={t('Shipping', 'Envío')}>
          <InfoCard>
            <InfoRow label={t('Shipped', 'Enviado')} value={shipping.shippedDate ? fmtDate(shipping.shippedDate) : null} />
            <InfoRow label={t('Carrier', 'Transportista')} value={shipping.carrier} />
          </InfoCard>
        </Section>
      )}

      {/* ── About ── */}
      {company && (
        <Section icon="🏢" title={t('About', 'Acerca de')}>
          <InfoCard>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{company.name}</div>
            {(company.city || company.state) && (
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                {[company.city, company.state].filter(Boolean).join(', ')}
              </div>
            )}
          </InfoCard>
        </Section>
      )}

      {/* ── Footer ── */}
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 11, marginTop: 32, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
        {t('Product information verified by quickLOG', 'Información del producto verificada por quickLOG')}
        <br />
        {t('Lot', 'Lote')} {batch?.lotCode || lotCode}
      </div>
    </div>
  );
}
